package com.qrkontaq.core.analytics

import com.qrkontaq.core.config.AppConfig
import com.qrkontaq.core.tenant.Tenant
import kotlinx.browser.document
import kotlinx.browser.localStorage
import kotlinx.browser.sessionStorage
import kotlinx.browser.window
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import org.w3c.dom.Element
import org.w3c.dom.events.Event
import org.w3c.dom.get
import org.w3c.dom.set
import org.w3c.dom.url.URLSearchParams
import kotlin.js.Date

@Serializable
private data class TrackedEvent(
    val type: String,
    val path: String,
    val target: String? = null,
    val referrer: String? = null,
    val utmSource: String? = null,
    val utmMedium: String? = null,
    val utmCampaign: String? = null,
    val language: String? = null,
    val screenW: Int? = null,
    val screenH: Int? = null,
)

@Serializable
private data class EventBatch(
    val visitorId: String,
    val sessionId: String,
    val site: String,
    val events: List<TrackedEvent>,
)

/**
 * First-party analytics tracker: pageviews + clicks for the landing, venue
 * admin and platform admin (guest menu is intentionally not tracked).
 * Anonymous — a random visitor id in localStorage, no PII. Events are batched
 * and shipped via navigator.sendBeacon as text/plain so the public ingestion
 * endpoint needs no CORS preflight.
 */
object Analytics {
    private const val VISITOR_KEY = "qk.vid"
    private const val SESSION_KEY = "qk.sid"
    private const val SESSION_SEEN_KEY = "qk.sat"
    private const val SESSION_GAP_MS = 30 * 60 * 1000
    private const val FLUSH_INTERVAL_MS = 15_000
    private const val FLUSH_THRESHOLD = 12

    private val json = Json { encodeDefaults = false }
    private val queue = mutableListOf<TrackedEvent>()
    private var started = false
    private var lastPageviewPath: String? = null
    private var sectionObserver: dynamic = null
    private val seenSections = mutableSetOf<String>()

    private val utm: Triple<String?, String?, String?> by lazy {
        val params = URLSearchParams(window.location.search)
        Triple(params.get("utm_source"), params.get("utm_medium"), params.get("utm_campaign"))
    }

    fun init() {
        if (started) return
        started = true
        if (isAutomation()) return

        installClickListener()
        installNavigationListeners()
        window.setInterval({ flush() }, FLUSH_INTERVAL_MS)
        document.addEventListener("visibilitychange", {
            if (document.asDynamic().visibilityState == "hidden") flush()
        })
        window.addEventListener("pagehide", { flush() })

        pageview(currentPath())
    }

    /** Records a pageview; consecutive duplicates are dropped. */
    fun pageview(path: String) {
        if (path == lastPageviewPath) return
        lastPageviewPath = path
        enqueue(
            TrackedEvent(
                type = "PAGEVIEW",
                path = path,
                referrer = document.referrer.takeIf { it.isNotEmpty() },
                utmSource = utm.first,
                utmMedium = utm.second,
                utmCampaign = utm.third,
                language = window.navigator.language,
                screenW = window.screen.width,
                screenH = window.screen.height,
            ),
        )
    }

    /**
     * Scroll-depth by section: fires SECTION_VIEW (target = element id) once per
     * page load when a section becomes ~a quarter visible. Safe to call again
     * after the DOM subtree is rebuilt (e.g. a landing language switch) — the
     * previous observer is dropped and already-seen sections stay deduplicated.
     */
    fun observeSections(ids: List<String>) {
        if (isAutomation()) return
        try {
            sectionObserver?.disconnect()
        } catch (_: Throwable) {
        }
        val observer =
            try {
                newIntersectionObserver { entries ->
                    for (entry in entries) {
                        if (entry.isIntersecting as Boolean) {
                            val id = (entry.target.id as? String).orEmpty()
                            if (id.isNotEmpty() && seenSections.add(id)) {
                                enqueue(TrackedEvent(type = "SECTION_VIEW", path = currentPath(), target = id))
                            }
                            try {
                                sectionObserver?.unobserve(entry.target)
                            } catch (_: Throwable) {
                            }
                        }
                    }
                }
            } catch (_: Throwable) {
                return // ancient browser without IntersectionObserver — skip silently
            }
        sectionObserver = observer
        ids.forEach { id ->
            document.getElementById(id)?.let { element ->
                if (id in seenSections) return@let
                observer.observe(element)
            }
        }
    }

    /** Landing language switch; target is "from>to" (e.g. "es>en"). */
    fun langSwitch(from: String, to: String) {
        if (from == to) return
        enqueue(TrackedEvent(type = "LANG_SWITCH", path = currentPath(), target = "$from>$to"))
    }

    @Suppress("UNUSED_PARAMETER")
    private fun newIntersectionObserver(callback: (Array<dynamic>) -> Unit): dynamic =
        js("new IntersectionObserver(callback, { threshold: 0.25 })")

    private fun enqueue(event: TrackedEvent) {
        if (resolveSite() == null) return
        queue.add(event)
        if (queue.size >= FLUSH_THRESHOLD) flush()
    }

    private fun flush() {
        if (queue.isEmpty()) return
        val site = resolveSite() ?: run { queue.clear(); return }
        val payload =
            json.encodeToString(
                EventBatch.serializer(),
                EventBatch(
                    visitorId = visitorId(),
                    sessionId = sessionId(),
                    site = site,
                    events = queue.toList(),
                ),
            )
        queue.clear()
        val url = AppConfig.getApiUrl("v1/public/analytics/events")
        val sent =
            try {
                window.navigator.asDynamic().sendBeacon(url, payload) as? Boolean ?: false
            } catch (_: Throwable) {
                false
            }
        if (!sent) {
            try {
                window.fetch(
                    url,
                    org.w3c.fetch.RequestInit(
                        method = "POST",
                        body = payload,
                    ).also { it.asDynamic().keepalive = true },
                )
            } catch (_: Throwable) {
                // fire-and-forget: analytics must never break the app
            }
        }
    }

    /** Where the visitor is: platform admin, any /admin surface, or the landing. */
    private fun resolveSite(): String? =
        when {
            Tenant.isPlatformAdminSite -> "PLATFORM"
            window.location.pathname.startsWith("/admin") -> "ADMIN"
            !Tenant.isTenantSite -> "LANDING"
            else -> null // guest menu — not tracked
        }

    private fun currentPath(): String = window.location.pathname

    private fun installClickListener() {
        document.addEventListener(
            "click",
            { event: Event ->
                val element = (event.target as? Element)?.closest("a,button,[role=button],[data-qk]")
                if (element != null) {
                    val label =
                        element.getAttribute("data-qk")
                            ?: element.getAttribute("aria-label")
                            ?: element.textContent?.trim()?.replace(Regex("\\s+"), " ")?.takeIf { it.isNotEmpty() }
                            ?: element.getAttribute("href")
                    if (label != null) {
                        enqueue(
                            TrackedEvent(
                                type = "CLICK",
                                path = currentPath(),
                                target = label.take(120),
                            ),
                        )
                    }
                }
            },
            true,
        )
    }

    /** SPA navigations: wrap pushState/replaceState + popstate so route changes auto-track. */
    private fun installNavigationListeners() {
        val history = window.history.asDynamic()
        val origPush = history.pushState
        val origReplace = history.replaceState
        val self = this
        // history.pushState/replaceState take exactly (state, title, url).
        history.pushState = { state: dynamic, title: dynamic, url: dynamic ->
            val result = origPush.call(history, state, title, url)
            self.pageview(self.currentPath())
            result
        }
        history.replaceState = { state: dynamic, title: dynamic, url: dynamic ->
            val result = origReplace.call(history, state, title, url)
            self.pageview(self.currentPath())
            result
        }
        window.addEventListener("popstate", { pageview(currentPath()) })
    }

    private fun visitorId(): String {
        localStorage[VISITOR_KEY]?.let { return it }
        val id = randomId()
        localStorage[VISITOR_KEY] = id
        return id
    }

    /** Session rotates after 30 minutes of inactivity. */
    private fun sessionId(): String {
        val now = Date.now().toLong()
        val lastSeen = sessionStorage[SESSION_SEEN_KEY]?.toLongOrNull() ?: 0
        var sid = sessionStorage[SESSION_KEY]
        if (sid == null || now - lastSeen > SESSION_GAP_MS) {
            sid = randomId()
            sessionStorage[SESSION_KEY] = sid
        }
        sessionStorage[SESSION_SEEN_KEY] = now.toString()
        return sid
    }

    private fun randomId(): String =
        try {
            (js("crypto.randomUUID()") as String).replace("-", "")
        } catch (_: Throwable) {
            (1..32).joinToString("") { "0123456789abcdef".random().toString() }
        }

    /** Playwright/Selenium sessions must not pollute the stats. */
    private fun isAutomation(): Boolean =
        try {
            window.navigator.asDynamic().webdriver == true
        } catch (_: Throwable) {
            false
        }
}
